/**
 * Payout time utilities.
 * Shared helpers for formatting time, merging time ranges,
 * and computing contextual feedback when no payouts are available.
 */

/**
 * Format seconds since midnight to HH:MM string.
 * @param {number} seconds
 * @returns {string} e.g. "08:30"
 */
export const formatTimeFromSeconds = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

/**
 * Merge overlapping/adjacent time ranges into non-overlapping sorted ranges.
 * @param {Array<{timeFrom: number, timeTo: number}>} ranges
 * @returns {Array<{timeFrom: number, timeTo: number}>}
 */
export const mergeTimeRanges = (ranges) => {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.timeFrom - b.timeFrom);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].timeFrom <= last.timeTo) {
      last.timeTo = Math.max(last.timeTo, sorted[i].timeTo);
    } else {
      merged.push({ ...sorted[i] });
    }
  }
  return merged;
};

/**
 * Compute a contextual feedback message when no payouts are available.
 * Returns null when there is nothing to report (payouts are available).
 *
 * @param {Object} opts
 * @param {Array}  opts.allPayouts      - Full payout list
 * @param {*}      opts.carTypeId       - Selected car type
 * @param {*}      opts.serviceCityId   - Service city
 * @param {Array<{timeFrom: number, timeTo: number}>} opts.occupiedRanges - Already-occupied time ranges
 * @returns {string|null}
 */
export const computePayoutFeedback = ({ allPayouts, carTypeId, serviceCityId, occupiedRanges }) => {
  // Check if ANY payouts exist for this carType + serviceCityId
  const matchingPayouts = allPayouts.filter(
    (p) => p.carTypeId === carTypeId && p.serviceCityId === serviceCityId
  );

  if (matchingPayouts.length === 0) {
    return "No payouts available for this car type";
  }

  const merged = mergeTimeRanges(occupiedRanges);
  const totalCovered = merged.reduce((sum, r) => sum + (r.timeTo - r.timeFrom), 0);

  if (totalCovered >= 86400) {
    return "All time slots are covered";
  }

  if (merged.length > 0) {
    const rangeStr = merged
      .map((r) => `${formatTimeFromSeconds(r.timeFrom)} - ${formatTimeFromSeconds(r.timeTo)}`)
      .join(", ");
    return `Existing payouts cover ${rangeStr}. No payout available for other times, kindly create one`;
  }

  return "No payouts available";
};
